import React, { useState } from "react";
import { Mail, Lock, XCircle, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../ui/LanguageSwitcher";

export default function LoginView({
  loginForm,
  setLoginForm,
  showPassword,
  setShowPassword,
  handleLogin,
  loading,
  message,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [localMessage, setLocalMessage] = useState({ type: "", text: "" });

  const validateLogin = () => {
    if (!loginForm.email) {
      setLocalMessage({ type: "error", text: t("validation.enterEmail") });
      return false;
    }
    if (!loginForm.password) {
      setLocalMessage({ type: "error", text: t("validation.enterPassword") });
      return false;
    }
    return true;
  };

  const onLoginClick = () => {
    setLocalMessage({ type: "", text: "" });
    if (!validateLogin()) return;
    handleLogin();
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 h-full w-full object-cover"
      >
        <source src="/login-bg.mp4" type="video/mp4" />
      </video>

      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70" />

      <div className="relative z-10 min-h-screen px-4">
        <div className="mx-auto flex w-full max-w-sm justify-end pt-6">
          <div className="rounded-2xl border border-white/15 bg-white/10 px-3 py-2 backdrop-blur-xl shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
            <LanguageSwitcher />
          </div>
        </div>

        {/* الكارد */}
        <div className="mx-auto flex min-h-[calc(100vh-88px)] w-full max-w-sm items-center justify-center pb-8">
          <div className="relative w-full overflow-hidden rounded-3xl border border-white/15 bg-white/10 backdrop-blur-xl shadow-[0_30px_90px_rgba(0,0,0,0.5)]">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-pink-500/10 via-violet-500/10 to-cyan-400/10" />
            <div className="pointer-events-none absolute -top-24 -end-24 h-64 w-64 rounded-full bg-pink-500/15 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -start-24 h-64 w-64 rounded-full bg-violet-500/15 blur-3xl" />

            <div className="relative p-8 text-center">
              {(localMessage.text || message?.text) && (
                <div className="mb-5 flex items-start gap-2 rounded-2xl border border-red-400/30 bg-red-500/15 p-3 text-sm text-red-100">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="text-start">{localMessage.text || message.text}</span>
                </div>
              )}

              <h1 className="text-3xl font-extrabold tracking-tight text-white drop-shadow">
                {t("common.appName")}
              </h1>

              <div className="mt-7 space-y-4">
                {/* Email */}
                <div className="text-start">
                  <div className="relative">
                    <Mail className="absolute start-3 top-1/2 h-5 w-5 -translate-y-1/2 text-white/70" />
                    <input
                      type="email"
                      value={loginForm.email}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, email: e.target.value })
                      }
                      placeholder={t("auth.login.emailPlaceholder")}
                      className="w-full rounded-2xl border border-white/15 bg-white/5 px-10 py-3 text-white placeholder-white/40 outline-none transition
                                 focus:border-pink-400/50 focus:bg-white/10 focus:ring-2 focus:ring-pink-400/30"
                      autoComplete="email"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="text-start">
                  <div className="relative">
                    <Lock className="absolute start-3 top-1/2 h-5 w-5 -translate-y-1/2 text-white/70" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={loginForm.password}
                      onChange={(e) =>
                        setLoginForm({ ...loginForm, password: e.target.value })
                      }
                      placeholder={t("auth.login.passwordPlaceholder")}
                      className="w-full rounded-2xl border border-white/15 bg-white/5 px-10 py-3 text-white placeholder-white/40 outline-none transition
                                 focus:border-pink-400/50 focus:bg-white/10 focus:ring-2 focus:ring-pink-400/30"
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 rounded-xl p-2 text-white/70 hover:bg-white/10 hover:text-white transition"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => navigate("/forgot-password")}
                      className="text-xs font-semibold text-white/75 hover:text-pink-200 transition"
                    >
                      {t("auth.login.forgotPassword")}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="button"
                  onClick={onLoginClick}
                  disabled={loading}
                  className="mt-1 w-full rounded-2xl bg-gradient-to-r from-pink-500 to-red-600 px-4 py-3 text-sm font-extrabold text-white
                             shadow-[0_18px_55px_rgba(236,72,153,0.25)] transition hover:opacity-95 active:scale-[0.99]
                             disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? t("auth.login.loading") : t("auth.login.submit")}
                </button>

                <p className="pt-1 text-center text-sm text-white/75">
                  {t("auth.login.noAccount")}{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/signup")}
                    className="font-extrabold text-pink-200 hover:text-pink-100 transition"
                  >
                    {t("auth.login.createAccount")}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="-mt-3 pb-6 text-center text-xs text-white/50">
          © {new Date().getFullYear()} SoSME
        </div>
      </div>
    </div>
  );
}
